import { CcfAlert, CcfBox, CcfSnackBar } from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCreateNewJourney,
  getSelectedContact,
  setSelectedChannel,
  setSelectedContact,
  setSelectedMedia,
} from '../+state/simulatorV2.slice';
import { BroadcastChannel } from 'broadcast-channel';
import SimulatorV2CreateNewJourney from '../simulator-v2-create-new-journey/simulator-v2-create-new-journey';
import SimulatorV2DashboardHeader from './simulator-v2-dashboard-header/simulator-v2-dashboard-header';
import { awsS3Config, generateDate, generateRandomId } from '../utils/utils';
import { useAgentName } from '../utils/useAgentName';
import AWS from 'aws-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import SimulatorV2AllJourneys from '../simulator-v2-allJourneys/simulator-v2-allJourneys';
import { contact } from '../+state/contact';
import CustomWorkspace from '../utils/custom-workspace';

/* eslint-disable-next-line */
export interface SimulatorV2DashboardProps {
  selectedAction: any;
}
/**
 * New Simulator Component to mimic the Call/Post feature from different sources
 * * @param props - its will take the list of conatct , current selected conatct and the onclick handler method
 * @returns
 * @example
 */
export function SimulatorV2Dashboard(props: SimulatorV2DashboardProps) {
  const { selectedAction } = props;
  const isCreateNewJourney = useSelector(getCreateNewJourney);
  const selectedContact: any = useSelector(getSelectedContact);
  const [updatedContact, setUpdatedContact] = useState(selectedContact);
  const [selectedRowData, setSelectedRowData]: any = useState([]);

  const [rowData, setRowData] = useState<Array<any>>([]);
  const [archivedRowData, setArchivedRowData] = useState<Array<any>>([]);
  const [updatedRowData, setUpdatedRowData] = useState<Array<any>>([]);
  const [archiveRowDataCopy, setArchiveRowDataCopy] = useState<Array<any>>([]);
  const [refreshJourneys, setRefreshJourneys] = useState();
  const [showSaveToast, setShowSaveToast] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
  const [isEditClick, setIsEditClick] = useState(false);
  const [selectedActionItem, setSelectedActionItem] = useState('');
  // Whether the Copilot tab's form currently passes validation. Defaults to
  // true so journeys without Copilot (or with an empty/hidden editor) save
  // normally; the Copilot tab flips it false when required fields are empty.
  const [isCopilotValid, setIsCopilotValid] = useState(true);
  // Build Manually's own working copy of the Copilot form. Kept here (not on
  // updatedContact) so it survives the Copilot tab remounting when switching
  // journey tabs, WITHOUT entering Redux (setSelectedContact) or the agent
  // broadcast payload ({ ...updatedContact }). Used only to repaint the
  // Build Manually editor; the simulation still reads updatedContact fields.
  const [copilotManualDraft, setCopilotManualDraft] = useState<any>(undefined);

  // Snapshots copilot contact fields per media/channel key so that when the
  // user switches media (which causes Journey Details to wipe
  // customerChatReplyJsonData) and then switches back, the saved copilot data
  // can be restored instead of showing the default template.
  // Key format: `${media}__${channel}`.
  const [copilotSnapshotByMedia, setCopilotSnapshotByMedia] = useState<Record<string, any>>({});
  // Tracks the previous updatedContact so we can detect media/channel changes
  // inside handleSetUpdatedContact before the state has been applied.
  const updatedContactRef = useRef<any>(updatedContact);

  // Keep the ref in sync with actual state.
  useEffect(() => {
    updatedContactRef.current = updatedContact;
  }, [updatedContact]);

  // Wrapper around setUpdatedContact that snapshots copilot fields when
  // media/channel is about to change.  Passed to child components in place of
  // the raw setter so all mutation paths are intercepted automatically.
  const handleSetUpdatedContact = useCallback((updater: any) => {
    const currentContact = updatedContactRef.current;
    const newContact =
      typeof updater === 'function' ? updater(currentContact) : updater;

    const mediaOrChannelChanged =
      currentContact?.media !== newContact?.media ||
      currentContact?.channel !== newContact?.channel;

    if (mediaOrChannelChanged && currentContact?.customerChatReplyJsonData?.length > 0) {
      const key = `${currentContact.media}__${currentContact.channel ?? ''}`;
      setCopilotSnapshotByMedia((prev) => ({
        ...prev,
        [key]: {
          media: currentContact.media,
          channel: currentContact.channel,
          customerChatReplyJsonData: currentContact.customerChatReplyJsonData,
          rtSummary: currentContact.rtSummary,
          autoSummaryText: currentContact.autoSummaryText,
          transferSummary: currentContact.transferSummary,
          autopilotSummary: currentContact.autopilotSummary,
          perviousInteractionJsonData: currentContact.perviousInteractionJsonData,
          botTranscriptData: currentContact.botTranscriptData,
        },
      }));
    }

    setUpdatedContact(updater);
  }, []);

  const dispatch = useDispatch();

  const agentName = useAgentName();

  /** @example showToast('Saved!') or showToast('Failed', 'error') */
  const showToast = (message: string, severity: 'success' | 'error' = 'success') => {
    setToastSeverity(severity);
    setShowSaveToast(message);
  };

  const s3Config = awsS3Config;

  const S3_BUCKET = s3Config.s3Bucket;
  // 'cxone-agent-demo-app';
  const OBJECT_KEY = agentName && selectedContact?.journeyName
    ? `simulatorDB/${agentName}/${selectedContact.journeyName}.json`
    : null;
  const CUSTOM_WORKSPACE_OBJECT_KEY = agentName
    ? `simulatorDB/${agentName}/custom-workspace/custom-workspace.json`
    : null;
  const initialObjectKey = `simulatorDB/`;

  AWS.config.update({
    accessKeyId: s3Config.accessKeyId,
    secretAccessKey: s3Config.secretAccessKey,
    region: s3Config.region,
  });

  const s3 = new AWS.S3();

  /**@example */
  const setupRowData = (objectContents: any, type: string) => {
    if (objectContents && objectContents.length > 0) {
      // Filter out undefined values and map to the desired format
      const rd = objectContents
        .filter((x: any) => x !== undefined)
        .map((x: any, i: any) => {
          return {
            ...x,
          };
        });
      if (type === 'save' || type === 'clone') {
        setRowData((prevRowData: any) => [...rd, ...prevRowData]);
        setUpdatedRowData((prevRowData: any) => [...rd, ...prevRowData]);
      } else if (type === 'initial') {
        // Separate archived and non-archived journeys
        const archivedJourneys = rd.filter(
          (journey: any) => journey.isArchived
        );
        const nonArchivedJourneys = rd.filter(
          (journey: any) => !journey.isArchived
        );

        setRowData([...nonArchivedJourneys]);
        setArchivedRowData([...archivedJourneys]);
        setArchiveRowDataCopy([...archivedJourneys]);
        setUpdatedRowData([...nonArchivedJourneys]);
      }
      // else if (type === 'clone') {
      //   setRowData((prevData: any) => [...prevData, ...rd]);
      //   setUpdatedRowData((prevData: any) => [...prevData, ...rd]);
      // }
    } else {
      setRowData([]);
      setUpdatedRowData([]);
    }
  };

  /**
   * @param panel- 'contacthistory'
   * @param event - changeevent
   * @param newExpanded - boolean
   * @example - fetchObjectFromS3(panel)
   */
  const fetchS3Object = async () => {
    const params = {
      Bucket: S3_BUCKET,
      Prefix: initialObjectKey,
    };

    try {
      const data = await s3.listObjectsV2(params).promise();
      const objects = data.Contents || [];

      // If you want to fetch the content of each object
      const objectContents = await Promise.all(
        objects
          .filter((object) => {
            // Ensure the key has text after the prefix
            const key = object.Key ?? '';
            return key.length > initialObjectKey.length;
          })
          .map(async (object) => {
            if (object.Key?.endsWith('.json')) {
              const objectParams = {
                Bucket: S3_BUCKET,
                Key: object.Key,
              };
              const objectData = await s3.getObject(objectParams).promise();

              const objectDataJson = JSON.parse(
                objectData.Body?.toString('utf-8') ?? ''
              );

              return objectDataJson[0];
            }
          })
      );

      setupRowData(objectContents, 'initial');
    } catch (error) {
      // Handle the error, e.g., show a message to the user
      setRowData([]);
      setUpdatedRowData([]);
    }
  };

  useEffect(() => {
    fetchS3Object();
  }, [refreshJourneys]);

  /**@example */
  const handleEditJourney = async (): Promise<boolean> => {
    if (!agentName) {
      showToast('Unable to save: user identity is not available. Please log in again.', 'error');
      return false;
    }
    if (!isCopilotValid) {
      showToast(
        'Please resolve the Copilot validation errors before saving.',
        'error'
      );
      return false;
    }
    const oldJourneyName = isEditClick ? selectedRowData[0]?.journeyName : null;
    const newJourneyName = selectedContact?.journeyName;
    const updatedDate = generateDate();

    // If editing and journeyName changed, delete the old object
    if (isEditClick && oldJourneyName && oldJourneyName !== newJourneyName) {
      const deleteParams = {
        Bucket: S3_BUCKET,
        Key: `simulatorDB/${agentName}/${oldJourneyName}.json`,
      };
      try {
        await s3.deleteObject(deleteParams).promise();
      } catch (err) {
        console.error('Error deleting old journey:', err);
      }
    }

    // Upload the new/updated journey
    const putParams = {
      Bucket: S3_BUCKET,
      Key: `simulatorDB/${agentName}/${newJourneyName}.json`,
      Body: JSON.stringify([
        { ...selectedContact, lastUpdatedDate: updatedDate },
      ]), // Wrap in an array to match the original structure
      ContentType: 'application/json',
    };

    try {
      await s3.putObject(putParams).promise();

      // Optimistically update rowData
      // Update rowData: if editing, combine selectedContact and previous rowData; else, keep previous rowData as is
      setRowData((prev: any) => {
        // Replace the row with the same journeyName as updatedContact, keep others as is
        return prev.map((row: any) =>
          row.id === updatedContact.id ? updatedContact : row
        );
      });
      setUpdatedRowData((prev: any) => {
        return prev.map((row: any) =>
          row.id === updatedContact.id ? updatedContact : row
        );
      });
      // Keep rowData as it was previously

      showToast(`${selectedContact?.journeyName} journey is successfully updated.`);

      setTimeout(() => {
        resetJourney();
      }, 1500);
      return true;
    } catch (err) {
      console.error('Error uploading journey:', err);
      return false;
    }
  };
  /**@example */
  const handleSaveJourney = async (): Promise<boolean> => {
    if (!agentName || !OBJECT_KEY) {
      showToast('Unable to save: user identity is not available. Please log in again.', 'error');
      return false;
    }
    if (!isCopilotValid) {
      showToast(
        'Please resolve the Copilot validation errors before saving.',
        'error'
      );
      return false;
    }
    const params = {
      Bucket: S3_BUCKET,
      Key: OBJECT_KEY,
      Body: JSON.stringify([selectedContact]), // The JSON string
      ContentType: 'application/json', // Set the content type
    };

    try {
      const data = await s3.putObject(params).promise();

      setupRowData([selectedContact], 'save');
      showToast(`${selectedContact?.journeyName} journey is successfully created.`);
      setTimeout(() => {
        setRefreshJourneys((r: any) => r + 1); // or whatever triggers the S3 fetch/remount
        resetJourney();
      }, 1500);
      return true;
    } catch (err) {
      console.error('Error', err);
      return false;
    }
  };

  /**
   * Function to make a call to the backend to update row data
   * @param handleIsFavourite - The updated row data
   * @example
   */
  const handleIsFavourite = async (updatedRow: {
    id: string;
    isFavourites: boolean;
    itemType: string;
    journeyName: string;
  }) => {
    if (!selectedRowData[0]?.createdBy || !selectedRowData[0]?.journeyName) {
      return;
    }
    const putParams = {
      Bucket: S3_BUCKET,
      Key: `simulatorDB/${selectedRowData[0].createdBy}/${selectedRowData[0].journeyName}.json`,
      Body: JSON.stringify([updatedRow]),
      ContentType: 'application/json',
    };

    try {
      await s3.putObject(putParams).promise();
      setRowData((prev: any) => {
        // Replace the row with the same journeyName as updatedContact, keep others as is
        return prev.map((row: any) =>
          row.id === updatedRow.id ? updatedRow : row
        );
      });
      setUpdatedRowData((prev: any) => {
        return prev.map((row: any) =>
          row.id === updatedRow.id ? updatedRow : row
        );
      });
      showToast(
        updatedRow.itemType === 'add'
          ? `${updatedRow?.journeyName} journey is added to Favourites.`
          : `${updatedRow?.journeyName} journey is removed from Favourites.`
      );
    } catch (err) {
      console.error('Error uploading journey:', err);
    }
  };

  /**
   * Function to handle the click event of the clone button
   * @param handleCloneJourney - The row data to be cloned
   * @example
   * handleCloneJourney(row,newJourneyName)
   */
  const handleCloneJourney = async (row: any, newJourneyName: string) => {
    if (!newJourneyName) {
      return;
    }
    if (!agentName) {
      showToast('Unable to clone: user identity is not available. Please log in again.', 'error');
      return;
    }

    // Generate a truly unique ID for the cloned journey
    const uniqueId =
      generateRandomId() +
      '-' +
      Date.now() +
      '-' +
      Math.random().toString(36).substring(2, 9);

    const clonedRow = {
      ...row,
      journeyName: newJourneyName,
      id: uniqueId,
      createdBy: agentName,
      isArchived: false,
      isFavourites: false,
      createdDate: generateDate(),
      lastUpdatedDate: generateDate(),
    };

    const params = {
      Bucket: S3_BUCKET,
      Key: `simulatorDB/${agentName}/${newJourneyName}.json`,
      Body: JSON.stringify([clonedRow]),
      ContentType: 'application/json',
    };

    try {
      await s3.putObject(params).promise();
      setupRowData([clonedRow], 'clone');
      showToast(`${newJourneyName} journey is successfully created.`);
    } catch (err) {
      console.error('Error cloning journey:', err);
    }
  };

  /**
   * Function to handle the archiving of a journey
   * @param row - The row data to be archived
   * @example
   */
  const handleArchiveJourney = async (row: any) => {
    if (!agentName) {
      showToast('Unable to archive: user identity is not available. Please log in again.', 'error');
      return;
    }
    const journeyName = row?.journeyName;
    const updatedRow = {
      ...row,
      isArchived: !row.isArchived,
    };

    const putParams = {
      Bucket: S3_BUCKET,
      Key: `simulatorDB/${agentName}/${journeyName}.json`,
      Body: JSON.stringify([updatedRow]),
      ContentType: 'application/json',
    };

    try {
      await s3.putObject(putParams).promise();
      if (updatedRow.isArchived) {
        setArchivedRowData((prev) => [...prev, updatedRow]);
        setRowData((prev) => prev.filter((r) => r.id !== updatedRow.id));

        setUpdatedRowData((prev) => prev.filter((r) => r.id !== updatedRow.id));
        setArchiveRowDataCopy((prev) => [...prev, updatedRow]);
      } else {
        // Restore from archived - update both states
        setArchivedRowData((prev) =>
          prev.filter((r) => r.id !== updatedRow.id)
        );
        setRowData((prev) => [...prev, updatedRow]);
        setUpdatedRowData((prev) => [...prev, updatedRow]);
        setArchiveRowDataCopy((prev) =>
          prev.filter((r) => r.id !== updatedRow.id)
        );
      }
      showToast(
        `${updatedRow.journeyName} journey is successfully ${
          updatedRow.isArchived ? 'archived' : 'restored'
        }.`
      );
    } catch (err) {
      console.error('Error archiving journey:', err);
    }
  };
  /**
   * Function to make a call to the backend to update row data
   * @param handleDeleteJourney - The updated row data
   * @example
   */
  const handleDeleteJourney = async (row: any) => {
    if (!agentName) {
      showToast('Unable to delete: user identity is not available. Please log in again.', 'error');
      return;
    }
    const oldJourneyName = row?.journeyName;
    const deleteParams = {
      Bucket: S3_BUCKET,
      Key: `simulatorDB/${agentName}/${oldJourneyName}.json`,
    };
    try {
      await s3.deleteObject(deleteParams).promise();

      setRowData((prev: any) => prev.filter((r: any) => r.id !== row.id));
      setUpdatedRowData((prev: any) =>
        prev.filter((r: any) => r.id !== row.id)
      );

      setArchivedRowData((prev: any) =>
        prev.filter((r: any) => r.id !== row.id)
      );

      setArchiveRowDataCopy((prev) => prev.filter((r) => r.id !== row.id));
      showToast(`${row?.journeyName} journey is successfully deleted.`);

      setTimeout(() => {
        resetJourney();
      }, 1500);
    } catch (err) {
      console.error('Error deleting journey:', err);
    }
  };

  /**@example */
  const getSelectedAction = () => {
    const commonJourneyProps = {
      updatedRowData,
      rowData,
      setRowData,
      refreshJourneys,
      setUpdatedContact,
      setUpdatedRowData,
      isEditClick,
      setIsEditClick,
      selectedRowData,
      setSelectedRowData,
      handleIsFavourite,
      handleArchiveJourney,
      handleDeleteJourney,
      setSelectedActionItem,
      handleCloneJourney,
      archivedRowData,
      selectedAction,
      setArchiveRowDataCopy,
      archiveRowDataCopy,
      setArchivedRowData,
    };

    let filteredRowData = [...rowData];

    switch (selectedAction) {
      case 'Crafted By Me':
        filteredRowData = rowData.filter(
          (item: any) => item.createdBy === agentName
        );
        break;
      case 'Favorites':
        filteredRowData = rowData.filter((item: any) => item.isFavourites);
        break;
      case 'Archived':
        filteredRowData = archivedRowData;
        break;
      case 'Custom Workspace':
        return <CustomWorkspace />;
      default:
        // Make sure to filter out archived journeys from All Journeys view
        filteredRowData = rowData;
        break;
    }

    return (
      <SimulatorV2AllJourneys
        {...commonJourneyProps}
        rowData={filteredRowData}
        selectedActionItem={selectedActionItem}
        setupRowData={setupRowData}
      />
    );
  };

  /**
   * Used to broadcast the message through 'simulator_channel' using broadcast channel
   * @example
   */
  const broadCastMessage = async () => {
    let selectedContactCopy = { ...updatedContact };
    selectedContactCopy = { ...selectedContactCopy, dateCreated: new Date() };

    if (selectedContactCopy.channel === 'Email') {
      const channel: BroadcastChannel<{ type: string; data: any }> =
        new BroadcastChannel('simulatorv2_channel');
      await channel.postMessage({
        type: 'insertContact',
        data: selectedContactCopy,
      });
      await channel.close();
      showToast(`${selectedContactCopy?.journeyName} journey was posted successfully`);
    } else {
      const channel: BroadcastChannel<{ type: string; data: any }> =
        new BroadcastChannel('simulatorv2_channel');
      await channel.postMessage({
        type: 'insertContact',
        data: selectedContactCopy,
      });
      await channel.close();
      showToast(`${selectedContactCopy?.journeyName} journey was posted successfully`);
    }
    setTimeout(() => {
      resetJourney();
    }, 1000);
  };

  useEffect(() => {
    if (
      selectedActionItem &&
      selectedActionItem.trim().toLowerCase() === 'start simulation'
    ) {
      broadCastMessage();
    }
  }, [selectedActionItem]);

  /**@example */
  const resetJourney = () => {
    setUpdatedContact(contact[0]);
    dispatch(setSelectedContact(contact[0]));
    setSelectedRowData([]);
    setIsEditClick(false);
    setSelectedActionItem('');
    setShowSaveToast('');
    setIsCopilotValid(true);
    setCopilotManualDraft(undefined);
    setCopilotSnapshotByMedia({});
    dispatch(setSelectedMedia('Voice'));
    dispatch(setSelectedChannel('Chat'));
  };

  /**@example */
  const handleCustomWorkspaceSave = async (): Promise<boolean> => {
    if (!agentName || !CUSTOM_WORKSPACE_OBJECT_KEY) {
      return false;
    }
    const params = {
      Bucket: S3_BUCKET,
      Key: CUSTOM_WORKSPACE_OBJECT_KEY,
      Body: JSON.stringify({
        customWorkspaceLabel1: selectedContact.customWorkspaceLabel1,
        customWorkspaceLabel2: selectedContact.customWorkspaceLabel2,
        customWorkspaceLabel3: selectedContact.customWorkspaceLabel3,
        customWorkspaceUrl1: selectedContact.customWorkspaceUrl1,
        customWorkspaceUrl2: selectedContact.customWorkspaceUrl2,
        customWorkspaceUrl3: selectedContact.customWorkspaceUrl3,
      }),
      ContentType: 'application/json',
    };

    try {
      await s3.putObject(params).promise();
      return true;
    } catch (err) {
      console.error('Error saving custom workspace:', err);
      return false;
    }
  };

  /**
   * to save custom workspace panel in local storage
   * @param args - none,
   * @example saveCustomWorkspaceHandler()
   * Save custom workspace details to local storage
   */
  const saveCustomWorkspaceHandler = async () => {
    let customWorkspacepanel = [
      {
        persistentPanelId: 1,
        persistentPanelLabel: selectedContact?.customWorkspaceLabel1,
        persistentPanelURI: selectedContact?.customWorkspaceUrl1,
      },
    ];
    if (selectedContact?.customWorkspaceLabel2.length > 0) {
      customWorkspacepanel = [
        ...customWorkspacepanel,
        {
          persistentPanelId: 2,
          persistentPanelLabel: selectedContact?.customWorkspaceLabel2,
          persistentPanelURI: selectedContact?.customWorkspaceUrl2,
        },
      ];
    }
    if (selectedContact?.customWorkspaceLabel3.length > 0) {
      customWorkspacepanel = [
        ...customWorkspacepanel,
        {
          persistentPanelId: 3,
          persistentPanelLabel: selectedContact?.customWorkspaceLabel3,
          persistentPanelURI: selectedContact?.customWorkspaceUrl3,
        },
      ];
    }
    localStorage.setItem(
      'customWorkspacePanel',
      JSON.stringify(customWorkspacepanel)
    );
    const saved = await handleCustomWorkspaceSave();
    showToast(
      saved
        ? 'Custom workspace saved successfully'
        : 'Unable to save custom workspace: user identity is not available. Please log in again.',
      saved ? 'success' : 'error'
    );
  };

  return (
    <CcfBox>
      <SimulatorV2DashboardHeader
        title={
          isEditClick && isCreateNewJourney
            ? 'Edit Journey'
            : isCreateNewJourney && !isEditClick
            ? 'Create New Journey'
            : selectedAction
        }
        archivedRowData={archivedRowData}
        handleSaveJourney={handleSaveJourney}
        handleEditJourney={handleEditJourney}
        rowData={rowData}
        isEditClick={isEditClick}
        resetJourney={resetJourney}
        selectedAction={selectedAction}
        saveCustomWorkspaceHandler={saveCustomWorkspaceHandler}
      />
      <CcfBox>
        {!isCreateNewJourney ? (
          getSelectedAction()
        ) : (
          <SimulatorV2CreateNewJourney
            rowData={rowData}
            updatedContact={updatedContact}
            setUpdatedContact={handleSetUpdatedContact}
            isEditClick={isEditClick}
            setIsCopilotValid={setIsCopilotValid}
            copilotManualDraft={copilotManualDraft}
            setCopilotManualDraft={setCopilotManualDraft}
            copilotSnapshotByMedia={copilotSnapshotByMedia}
          />
        )}
      </CcfBox>
      <CcfBox>
        <CcfSnackBar
          open={showSaveToast !== ''}
          autoHideDuration={3000}
          onClose={() => {
            setShowSaveToast('');
            setToastSeverity('success');
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <div>
            <CcfAlert
              closeAlert={() => {
                setShowSaveToast('');
                setToastSeverity('success');
              }}
              severity={toastSeverity}
              style={{
                height: 'auto',
                width: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showSaveToast}
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
    </CcfBox>
  );
}
export default SimulatorV2Dashboard;
